import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// PositionTile.jsx - OPTIMIZADO COMPLETO
// ✅ FIX 1: React.memo - Solo re-render si props de ESTA posición cambian
// ✅ FIX 2: Shimmer SOLO en primera carga, NO en updates
// ✅ FIX 3: Twinkle suave cuando cambia el valor

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Popper,
  Fade,
  Card,
  CardContent,
  Divider,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Speed as PressureIcon,
  Thermostat as TemperatureIcon,
  Sensors as SensorIcon,
  AccessTime as TimeIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';

import { useUserTimezone } from '../../hooks/useUserTimezone';
import { useUnitPreferences } from '../../hooks/useUnitPreferences';
import { formatPressureValue, formatTemperatureValue } from '../../utils/unitFormat';

// ENHANCED: Smart date formatting with user timezone
const formatSmartDateTime = (timestamp, formatUserDateTimeSync) => {
  if (!timestamp) return '';

  try {
    return formatUserDateTimeSync(timestamp, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('❌ Error formatting timestamp with user timezone:', error, timestamp);

    try {
      let date;
      if (typeof timestamp === 'string') {
        if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
          const utcTimestamp = timestamp.includes('T') ? timestamp + 'Z' : timestamp + 'T00:00:00Z';
          date = new Date(utcTimestamp);
        } else {
          date = new Date(timestamp);
        }
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) {
        console.warn('⚠️ Invalid timestamp format:', timestamp);
        return timestamp.toString();
      }

      const formatter = new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      return formatter.format(date);
    } catch (fallbackError) {
      console.error('❌ Fallback formatting also failed:', fallbackError);
      return timestamp.toString();
    }
  }
};

const calculateHoursSinceTimestamp = (timestamp) => {
  if (!timestamp) return null;

  try {
    let date;
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        const utcTimestamp = timestamp.includes('T') ? timestamp + 'Z' : timestamp + 'T00:00:00Z';
        date = new Date(utcTimestamp);
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      console.warn('⚠️ Invalid timestamp for hours calculation:', timestamp);
      return null;
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const hours = diffMs / (1000 * 60 * 60);

    if (hours < 0) {
      console.warn('⚠️ Timestamp is in the future:', timestamp);
      return 0;
    }

    return hours;
  } catch (error) {
    console.error('❌ Error calculating hours since timestamp:', error, timestamp);
    return null;
  }
};

const formatHoursDisplay = (hours) => {
  if (hours === null || hours === undefined) return '--';

  if (hours < 1) {
    const minutes = Math.floor(hours * 60);
    return `${minutes}m`;
  }

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours < 1) {
      return `${days}d`;
    }
    return `${days}d${Math.floor(remainingHours)}h`;
  }

  return hours.toFixed(1) + 'h';
};

const getAxleNumberForPosition = (position, vehicleConfiguration) => {
  if (!vehicleConfiguration || !position) return null;

  const axleCounts = vehicleConfiguration.split(' x ').map(x => parseInt(x.trim()));

  let currentPosition = 1;
  for (let axleIndex = 0; axleIndex < axleCounts.length; axleIndex++) {
    const axlePositions = axleCounts[axleIndex];
    const axleStartPos = currentPosition;
    const axleEndPos = currentPosition + axlePositions - 1;

    if (position >= axleStartPos && position <= axleEndPos) {
      return axleIndex + 1;
    }

    currentPosition += axlePositions;
  }

  return null;
};

const PositionTile = ({
  vehicle,
  position,
  realtimeData,
  lastAvailableData,
  alarms = [],
  alarmStatuses = [],
  positionTickets = [],
  onTelemetryClick,
  isInitialLoading = false,
  isLoadingData = false,
  displayMode = 'pressure',
  // Modo "Presión en frío": `coldPosition` viene del motor central del backend
  // ({psi, temp, psi_cold, ideal, status, delta, summary, action, stale, ...}).
  coldMode = false,
  coldPosition = null,
  // Cuando es true (porque el padre ya dibuja el delineado del par Yoyo),
  // este tile NO pinta su propio borde rojo individual.
  suppressYoyoOutline = false,
  // Modo PLANO (esquematico del detalle del ticket): no hay telemetria viva.
  // Sin esto el tile se queda en "cargando" para siempre, porque su shimmer
  // solo se apaga cuando llegan datos que aqui nunca van a llegar.
  staticSnapshot = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  // 🔥 FIX 2: Estados separados para carga inicial vs updates
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [showInitialShimmer, setShowInitialShimmer] = useState(true);

  const [shouldTwinkle, setShouldTwinkle] = useState(false);
  const prevPressureValue = useRef(null);
  const hasReceivedFirstData = useRef(false);

  // Preferencias de unidades COMPARTIDAS (una sola petición para todo el panel,
  // no una por cada tile). El hook cachea y notifica cambios.
  const { preferences: unitPreferences, loading: unitPreferencesLoading } = useUnitPreferences();

  const { formatUserDateTimeSync } = useUserTimezone();

  // 🔥 FIX 2: Detectar primera carga de datos
  useEffect(() => {
    const hasData = realtimeData || lastAvailableData;

    if (hasData && !hasReceivedFirstData.current) {
      hasReceivedFirstData.current = true;
      setIsFirstLoad(false);

      // Apagar shimmer inicial después de 300ms
      const timer = setTimeout(() => {
        setShowInitialShimmer(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [realtimeData, lastAvailableData]);


  // Unit conversion utilities
  const unitConversionUtils = {
    convertPressure: (value, fromUnit = 'PSI', toUnit = null) => {
      if (!value || isNaN(value)) return null;

      const targetUnit = toUnit || unitPreferences?.pressure_unit || 'PSI';
      if (fromUnit === targetUnit) return value;

      let psiValue = value;
      if (fromUnit === 'BAR') {
        psiValue = value * 14.5038;
      } else if (fromUnit === 'kPa') {
        psiValue = value * 0.145038;
      }

      if (targetUnit === 'BAR') {
        return psiValue / 14.5038;
      } else if (targetUnit === 'kPa') {
        return psiValue / 0.145038;
      }

      return psiValue;
    },

    convertTemperature: (value, fromUnit = 'C', toUnit = null) => {
      if (!value || isNaN(value)) return null;

      const targetUnit = toUnit || unitPreferences?.temperature_unit || 'C';
      if (fromUnit === targetUnit) return value;

      let celsiusValue = value;
      if (fromUnit === 'F') {
        celsiusValue = (value - 32) * 5 / 9;
      } else if (fromUnit === 'K') {
        celsiusValue = value - 273.15;
      }

      if (targetUnit === 'F') {
        return celsiusValue * 9 / 5 + 32;
      } else if (targetUnit === 'K') {
        return celsiusValue + 273.15;
      }

      return celsiusValue;
    }
  };

  const getUnitSymbol = (unit) => {
    const symbols = {
      'PSI': 'PSI',
      'BAR': 'bar',
      'kPa': 'kPa',
      'kg/cm2': 'kg/cm²',
      'atm': 'atm',
      'hPa': 'hPa',
      'mmHg': 'mmHg',
      'inHg': 'inHg',
      'C': '°C',
      'F': '°F',
      'K': 'K',
      'R': '°R'
    };

    return symbols[unit] || unit;
  };

  const getConvertedValues = (rawData) => {
    if (!rawData || !unitPreferences) {
      return {
        pressure: rawData?.pressure || null,
        temperature: rawData?.temperature || null,
        pressureUnit: 'PSI',
        temperatureUnit: '°C'
      };
    }

    const convertedPressure = rawData.pressure !== null && rawData.pressure !== undefined
      ? unitConversionUtils.convertPressure(rawData.pressure, 'PSI')
      : null;

    const convertedTemperature = rawData.temperature !== null && rawData.temperature !== undefined
      ? unitConversionUtils.convertTemperature(rawData.temperature, 'C')
      : null;

    return {
      pressure: convertedPressure,
      temperature: convertedTemperature,
      pressureUnit: getUnitSymbol(unitPreferences.pressure_unit),
      temperatureUnit: getUnitSymbol(unitPreferences.temperature_unit)
    };
  };

  const assignedSensor = vehicle?.vehicle_sensors?.find(vs => vs.position === position);
  const hasAssignment = !!assignedSensor;

  const effectiveData = realtimeData || lastAvailableData;
  const isUsingLastAvailable = !realtimeData && lastAvailableData;
  const isRealtimeFromWebSocket = realtimeData?.isRealtime === true;

  const convertedValues = getConvertedValues(effectiveData);
  const axleNumber = getAxleNumberForPosition(position, vehicle?.configuration_p);

  // Razones que NO deben pintar la celda en rojo (revisamos AMBOS campos para ser robustos
  // ante variaciones del payload: alarm_reason, metric_type, breach_type).
  const NON_FILL_TICKET_REASONS = new Set([
    'dual_yoyo',
    'sensor_no_report',
    'receiver_no_report',
    'NO_REPORT',           // metric_type del monitor de falta de reporte
    'no_report',           // por si llega lowercase
  ]);
  const _isTicketFill = (ticket) => {
    if (!ticket) return false;
    const candidates = [ticket.alarm_reason, ticket.metric_type, ticket.breach_type]
      .filter(Boolean)
      .map((s) => String(s));
    // Si CUALQUIERA de los identificadores indica no-fill, no pintamos rojo.
    if (candidates.some((c) => NON_FILL_TICKET_REASONS.has(c))) return false;
    return true;
  };

  const positionOpenTickets = (positionTickets?.filter(ticket => ticket.status === 'open') || []).filter(_isTicketFill);
  const positionAcknowledgedTickets = (positionTickets?.filter(ticket => ticket.status === 'acknowledged') || []).filter(_isTicketFill);

  // Para el overlay de badges/conteo, sí queremos saber si hay tickets de cualquier tipo.
  const positionOpenTicketsAll = positionTickets?.filter(ticket => ticket.status === 'open') || [];
  const positionAcknowledgedTicketsAll = positionTickets?.filter(ticket => ticket.status === 'acknowledged') || [];

  // ¿Hay una ALERTA REAL pendiente? (PSI/TEMP/etc, no no_report ni dual_yoyo)
  // Solo los tickets que pasaron el filtro _isTicketFill cuentan como alerta real.
  const _hasRealAlertPending = (positionOpenTickets.length > 0) || (positionAcknowledgedTickets.length > 0);

  // ¿El sensor está sin reportar? (sin data fresca o usando lastAvailable/old-data)
  // Usamos esto luego para decidir si parpadear gris/rojo.
  const _hasNoReport = (!realtimeData) || isUsingLastAvailable ||
    (realtimeData?.dataAge && realtimeData.dataAge > 48 * 60 * 60 * 1000);

  const hoursSinceUpdate = displayMode === 'hours' ? calculateHoursSinceTimestamp(effectiveData?.timestamp) : null;
  const formattedHours = displayMode === 'hours' ? formatHoursDisplay(hoursSinceUpdate) : null;

  // 🔥 FIX 3: Detectar cambios de valor para twinkle (SIN shimmer)
  useEffect(() => {
    // Solo hacer twinkle si ya NO estamos en carga inicial
    if (!isFirstLoad && !showInitialShimmer && !isInitialLoading && !isLoadingData && !unitPreferencesLoading) {
      const currentPressure = convertedValues.pressure;
      const previousPressure = prevPressureValue.current;

      if (currentPressure !== null && currentPressure !== undefined &&
        previousPressure !== null && previousPressure !== undefined &&
        Math.abs(currentPressure - previousPressure) > 0.05) {

        console.log(`✨ Position ${position}: Pressure changed ${previousPressure.toFixed(1)} → ${currentPressure.toFixed(1)} ${convertedValues.pressureUnit} - TWINKLE!`);

        setShouldTwinkle(true);
        const timer = setTimeout(() => {
          setShouldTwinkle(false);
        }, 1200);

        return () => clearTimeout(timer);
      }

      prevPressureValue.current = currentPressure;
    }
  }, [convertedValues.pressure, realtimeData, position, isFirstLoad, showInitialShimmer, isInitialLoading, isLoadingData, unitPreferencesLoading, convertedValues.pressureUnit]);

  const getPositionAlarmStatuses = () => {
    if (!alarmStatuses || !Array.isArray(alarmStatuses) || !hasAssignment || !axleNumber) {
      return [];
    }

    const positionAlarms = alarmStatuses.filter(alarmStatus =>
      alarmStatus.vehicle_id === vehicle?.id &&
      alarmStatus.axle === axleNumber &&
      Number(alarmStatus.position) === Number(position)
    );

    return positionAlarms;
  };

  const positionAlarmStatuses = getPositionAlarmStatuses();

  const severityRank = {
    normal: 0,
    alarm: 1,
    critical: 2,
    extreme: 3
  };

  // Reasons that must NOT paint the tile background red (they only adorn it):
  //   - dual_yoyo: error de hermandad/yoyo → solo borde rojo con delineado negro
  //   - sensor_no_report / receiver_no_report: falta de reporte → no rellenar, sin alarma de color
  const NON_FILL_REASONS = new Set(['dual_yoyo', 'sensor_no_report', 'receiver_no_report']);

  const _reasonsByPosition = positionAlarmStatuses.flatMap((alarm) =>
    (alarm.active_reasons?.length
      ? alarm.active_reasons.map((r) => r.alarm_reason || r.reason)
      : alarm.alarm_reason
        ? [alarm.alarm_reason]
        : []
    ).filter(Boolean)
  );
  const _allReasonsAreNonFill = _reasonsByPosition.length > 0 &&
    _reasonsByPosition.every((reason) => NON_FILL_REASONS.has(reason));

  // No-report alone: this position has ONLY no_report-style reasons → don't paint fill.
  const _onlyNoReport = _reasonsByPosition.length > 0 &&
    _reasonsByPosition.every((r) => r === 'sensor_no_report' || r === 'receiver_no_report');

  // YOYO group highlighting: a position is "in an active yoyo group" if any
  // alarm status on the same vehicle has alarm_reason='dual_yoyo' and lists
  // this position in its group_positions array. The triggering position has
  // its own alarm; the *peer* positions in the group only need the highlight.
  const yoyoGroupInfo = (() => {
    if (!alarmStatuses || !Array.isArray(alarmStatuses) || !hasAssignment) {
      return { inGroup: false, severity: null, isTriggerPosition: false, peers: [] };
    }
    let inGroup = false;
    let severity = null;
    let isTriggerPosition = false;
    let peers = [];
    for (const st of alarmStatuses) {
      if (st.vehicle_id !== vehicle?.id) continue;
      const reasons = (st.active_reasons || []).filter(r => r.alarm_reason === 'dual_yoyo');
      const yoyoReason = reasons[0] || (st.alarm_reason === 'dual_yoyo' ? { severity: st.severity } : null);
      if (!yoyoReason) continue;
      const group = yoyoReason.group_positions || st.group_positions || [];
      if (!Array.isArray(group) || group.length === 0) continue;
      const groupNumbers = group.map(Number);
      if (groupNumbers.includes(Number(position))) {
        inGroup = true;
        if (Number(st.position) === Number(position)) isTriggerPosition = true;
        const sev = (yoyoReason.severity || st.severity || 'alarm').toLowerCase();
        const rank = severityRank[sev] || 1;
        const currentRank = severity ? (severityRank[severity] || 0) : 0;
        if (rank > currentRank) severity = sev;
        peers = groupNumbers.filter(p => p !== Number(position));
      }
    }
    return { inGroup, severity, isTriggerPosition, peers };
  })();

  const getAlarmSeverity = (alarm) => {
    if (!alarm) return 'normal';
    const activeSeverities = (alarm.active_reasons || []).map(reason => `${reason.severity || ''}`.toLowerCase());
    const explicitSeverity = `${alarm.severity || alarm.status || 'normal'}`.toLowerCase();
    const candidates = [...activeSeverities, explicitSeverity === 'warning' ? 'alarm' : explicitSeverity === 'critical' ? 'critical' : explicitSeverity];
    return candidates.reduce((highest, current) => (severityRank[current] > severityRank[highest] ? current : highest), 'normal');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'extreme':
      case 'critical':
        return 'error.main';
      case 'alarm':
        return 'warning.main';
      default:
        return 'success.main';
    }
  };

  const activeReasonBadges = positionAlarmStatuses.flatMap((alarm) => {
    const reasons = alarm.active_reasons?.length
      ? alarm.active_reasons
      : alarm.alarm_reason
        ? [{ reason: alarm.alarm_reason, severity: alarm.severity }]
        : [];

    return reasons
      .filter((reason) => {
        const r = reason.alarm_reason || reason.reason;
        // No mostrar texto/etiqueta para Dual (la indicación es solo borde rojo + outline negro),
        // ni para no-report (se marca con borde discreto).
        return r !== 'dual_yoyo' && r !== 'sensor_no_report' && r !== 'receiver_no_report';
      })
      .map((reason, index) => {
        const r = reason.alarm_reason || reason.reason;
        return {
          key: `${alarm.metric_type || t('hardcoded.components_schematics_positiontile.metric')}-${r || t('hardcoded.components_schematics_positiontile.reason')}-${index}`,
          label: r === 'thermal_risk'
            ? t('hardcoded.components_schematics_positiontile.thermal')
            : r === 'pressure_threshold'
              ? 'PSI'
              : r === 'temperature_threshold'
                ? t('hardcoded.components_schematics_positiontile.temp')
                : r || t('hardcoded.components_schematics_positiontile.alerta'),
          severity: `${reason.severity || alarm.severity || 'alarm'}`.toLowerCase()
        };
      });
  });

  const getMetricColorAndStatus = (metricType, currentValue) => {
    if (!currentValue && currentValue !== 0) {
      return {
        color: 'text.secondary',
        status: t('hardcoded.components_schematics_positiontile.no-data'),
        limits: null
      };
    }

    if (isUsingLastAvailable) {
      return {
        color: 'grey.600',
        status: t('hardcoded.components_schematics_positiontile.historical'),
        limits: null
      };
    }

    const metricAlarm = positionAlarmStatuses.find(alarm =>
      alarm.metric_type === (metricType === 'pressure' ? 'PSI' : 'TEMP')
    );

    if (!metricAlarm) {
      return {
        color: 'secondary.main',
        status: 'no-alarm-profile',
        limits: null
      };
    }

    let convertedMin = metricAlarm.threshold_min;
    let convertedMax = metricAlarm.threshold_max;
    let displayUnit = metricAlarm.unit;

    if (unitPreferences && metricType === 'pressure') {
      if (convertedMin !== null) {
        convertedMin = unitConversionUtils.convertPressure(metricAlarm.threshold_min, 'PSI');
      }
      if (convertedMax !== null) {
        convertedMax = unitConversionUtils.convertPressure(metricAlarm.threshold_max, 'PSI');
      }
      displayUnit = getUnitSymbol(unitPreferences.pressure_unit);
    } else if (unitPreferences && metricType === 'temperature') {
      if (convertedMin !== null) {
        convertedMin = unitConversionUtils.convertTemperature(metricAlarm.threshold_min, 'C');
      }
      if (convertedMax !== null) {
        convertedMax = unitConversionUtils.convertTemperature(metricAlarm.threshold_max, 'C');
      }
      displayUnit = getUnitSymbol(unitPreferences.temperature_unit);
    }

    const isOutOfBounds = (
      (convertedMin !== null && currentValue < convertedMin) ||
      (convertedMax !== null && currentValue > convertedMax)
    );

    const alarmSeverity = getAlarmSeverity(metricAlarm);
    const hasActiveReason = (metricAlarm.active_reasons || []).length > 0 || alarmSeverity !== 'normal';

    return {
      color: hasActiveReason ? getSeverityColor(alarmSeverity) : isOutOfBounds ? 'error.main' : 'success.main',
      status: hasActiveReason ? alarmSeverity : isOutOfBounds ? 'alarm' : 'normal',
      limits: {
        min: convertedMin,
        max: convertedMax,
        unit: displayUnit
      }
    };
  };

  const checkAlarmStatusWithTickets = () => {
    if (isLoadingData || unitPreferencesLoading) {
      return null;
    }

    if (positionOpenTickets.length > 0) {
      const ticket = positionOpenTickets[0];
      return {
        triggered: true,
        ticket: ticket,
        currentValue: ticket.current_value,
        metric: ticket.metric_type,
        source: 'ticket_open'
      };
    }

    if (positionAcknowledgedTickets.length > 0) {
      const ticket = positionAcknowledgedTickets[0];
      return {
        triggered: true,
        ticket: ticket,
        currentValue: ticket.current_value,
        metric: ticket.metric_type,
        source: 'ticket_acknowledged'
      };
    }

    if (!effectiveData || positionAlarmStatuses.length === 0) {
      return null;
    }

    const psiAlarm = positionAlarmStatuses.find(alarm => alarm.metric_type === 'PSI');
    if (psiAlarm && convertedValues.pressure !== null && convertedValues.pressure !== undefined) {
      let convertedMin = psiAlarm.threshold_min;
      let convertedMax = psiAlarm.threshold_max;

      if (unitPreferences) {
        if (convertedMin !== null) {
          convertedMin = unitConversionUtils.convertPressure(psiAlarm.threshold_min, 'PSI');
        }
        if (convertedMax !== null) {
          convertedMax = unitConversionUtils.convertPressure(psiAlarm.threshold_max, 'PSI');
        }
      }

      const isOutOfBounds = (
        (convertedMin !== null && convertedValues.pressure < convertedMin) ||
        (convertedMax !== null && convertedValues.pressure > convertedMax)
      );

      const psiSeverity = getAlarmSeverity(psiAlarm);
      if (psiSeverity !== 'normal' || isOutOfBounds) {
        return {
          triggered: true,
          alarm: psiAlarm,
          currentValue: convertedValues.pressure,
          breachType: convertedValues.pressure < convertedMin ? 'MIN' : 'MAX',
          metric: 'PSI',
          severity: psiSeverity,
          source: 'alarm_profile'
        };
      }
    }

    const tempAlarm = positionAlarmStatuses.find(alarm => alarm.metric_type === 'TEMP');
    if (tempAlarm && convertedValues.temperature !== null && convertedValues.temperature !== undefined) {
      let convertedMin = tempAlarm.threshold_min;
      let convertedMax = tempAlarm.threshold_max;

      if (unitPreferences) {
        if (convertedMin !== null) {
          convertedMin = unitConversionUtils.convertTemperature(tempAlarm.threshold_min, 'C');
        }
        if (convertedMax !== null) {
          convertedMax = unitConversionUtils.convertTemperature(tempAlarm.threshold_max, 'C');
        }
      }

      const isOutOfBounds = (
        (convertedMin !== null && convertedValues.temperature < convertedMin) ||
        (convertedMax !== null && convertedValues.temperature > convertedMax)
      );

      const tempSeverity = getAlarmSeverity(tempAlarm);
      if (tempSeverity !== 'normal' || isOutOfBounds) {
        return {
          triggered: true,
          alarm: tempAlarm,
          currentValue: convertedValues.temperature,
          breachType: convertedValues.temperature < convertedMin ? 'MIN' : 'MAX',
          metric: 'TEMP',
          severity: tempSeverity,
          source: 'alarm_profile'
        };
      }
    }

    return { triggered: false };
  };

  const alarmStatus = checkAlarmStatusWithTickets();

  const getExpectedStateColor = () => {
    if (!hasAssignment) {
      return {
        bgColor: theme.palette.grey[400],
        borderColor: theme.palette.grey[400],
        status: t('hardcoded.components_schematics_positiontile.no-sensor')
      };
    }

    if (positionOpenTickets.length > 0) {
      return {
        bgColor: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        status: t('hardcoded.components_schematics_positiontile.ticket-alarm')
      };
    }

    if (positionAcknowledgedTickets.length > 0) {
      return {
        bgColor: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        status: t('hardcoded.components_schematics_positiontile.ticket-acknowledged')
      };
    }

    const highestAlarmSeverity = positionAlarmStatuses.reduce(
      (highest, alarm) => {
        const current = getAlarmSeverity(alarm);
        return severityRank[current] > severityRank[highest] ? current : highest;
      },
      'normal'
    );

    if (highestAlarmSeverity !== 'normal') {
      return {
        bgColor: highestAlarmSeverity === 'alarm' ? theme.palette.warning.main : theme.palette.error.main,
        borderColor: highestAlarmSeverity === 'alarm' ? theme.palette.warning.main : theme.palette.error.main,
        status: 'alarm'
      };
    }

    if (effectiveData && (convertedValues.pressure !== null && convertedValues.pressure !== undefined)) {
      if (isUsingLastAvailable) {
        return {
          bgColor: theme.palette.grey[500],
          borderColor: theme.palette.grey[500],
          status: t('hardcoded.components_schematics_positiontile.last-available')
        };
      }

      if (realtimeData?.dataAge && realtimeData.dataAge > 48 * 60 * 60 * 1000) {
        return {
          bgColor: theme.palette.grey[500],
          borderColor: theme.palette.grey[500],
          status: 'old-data'
        };
      } else {
        return {
          bgColor: theme.palette.success.main,
          borderColor: theme.palette.success.main,
          status: 'normal'
        };
      }
    }

    return {
      bgColor: theme.palette.success.main,
      borderColor: theme.palette.success.main,
      status: 'normal'
    };
  };

  const getTileStatus = () => {
    if (!hasAssignment) {
      return {
        color: theme.palette.common.white,
        bgColor: theme.palette.grey[400],
        borderColor: theme.palette.grey[400],
        status: t('hardcoded.components_schematics_positiontile.no-sensor')
      };
    }

    // 🔥 FIX: Solo mostrar shimmer si es primera carga Y no han llegado datos
    if (!staticSnapshot && (isFirstLoad || isInitialLoading) && showInitialShimmer && !effectiveData) {
      const expectedColors = getExpectedStateColor();
      return {
        color: theme.palette.common.white,
        bgColor: expectedColors.bgColor,
        borderColor: expectedColors.borderColor,
        status: 'loading',
        shimmer: true,
        expectedStatus: expectedColors.status
      };
    }

    if (alarmStatus?.triggered) {
      // Si la alarma activa proviene SOLO de razones de no-fill (dual_yoyo / no_report),
      // no pintamos el fondo en rojo. La decoración visual la añade el borde/outline más abajo.
      if (_allReasonsAreNonFill) {
        // Caer al estado normal con datos (o gris si no hay datos) y dejar que el outline
        // marque Dual o que un borde sutil marque no-report.
      } else {
        return {
          color: theme.palette.common.white,
          bgColor: theme.palette.error.main,
          borderColor: theme.palette.error.main,
          status: alarmStatus.source === 'ticket_open' ? t('hardcoded.components_schematics_positiontile.ticket-alarm') :
            alarmStatus.source === 'ticket_acknowledged' ? t('hardcoded.components_schematics_positiontile.ticket-acknowledged') : 'alarm',
          pulse: alarmStatus.source === 'ticket_open'
        };
      }
    }

    if (effectiveData && (convertedValues.pressure !== null && convertedValues.pressure !== undefined)) {
      if (isUsingLastAvailable) {
        return {
          color: theme.palette.common.white,
          bgColor: theme.palette.grey[500],
          borderColor: theme.palette.grey[500],
          status: t('hardcoded.components_schematics_positiontile.last-available')
        };
      }

      if (realtimeData?.dataAge && realtimeData.dataAge > 48 * 60 * 60 * 1000) {
        return {
          color: theme.palette.common.white,
          bgColor: theme.palette.grey[500],
          borderColor: theme.palette.grey[500],
          status: 'old-data'
        };
      } else {
        return {
          color: theme.palette.common.white,
          bgColor: theme.palette.success.main,
          borderColor: theme.palette.success.main,
          status: 'normal',
          twinkle: shouldTwinkle
        };
      }
    }

    return {
      color: theme.palette.common.white,
      bgColor: theme.palette.grey[600],
      borderColor: theme.palette.grey[600],
      status: t('hardcoded.components_schematics_positiontile.no-data')
    };
  };

  const handleMouseEnter = (event) => {
    if (hasAssignment && !showInitialShimmer && !isLoadingData && !unitPreferencesLoading) {
      setAnchorEl(event.currentTarget);
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

  const handleClick = () => {
    if (hasAssignment && onTelemetryClick && !showInitialShimmer && !isLoadingData && !unitPreferencesLoading) {
      // Se pasa la posicion para que la grafica del modal abra filtrada por ella.
      onTelemetryClick(vehicle, position);
    }
  };

  const formatValue = (value, metric) => {
    if (value === null || value === undefined) return '--';

    if (!unitPreferences) {
      switch (metric) {
        case 'pressure': return `${value.toFixed(1)} PSI`;
        case 'temperature': return `${value.toFixed(1)}°C`;
        default: return value.toFixed(1);
      }
    }

    const globalDecimals = parseInt(unitPreferences.decimal_places) || 2;
    const decimalMap = unitPreferences.decimal_places_map || unitPreferences.display_settings || {};
    const pressureDecimals = Number.isInteger(decimalMap?.pressure) ? decimalMap.pressure : Number.isInteger(decimalMap?.pressure?.decimals) ? decimalMap.pressure.decimals : globalDecimals;
    const temperatureDecimals = Number.isInteger(decimalMap?.temperature) ? decimalMap.temperature : Number.isInteger(decimalMap?.temperature?.decimals) ? decimalMap.temperature.decimals : 1;

    switch (metric) {
      case 'pressure':
        return `${value.toFixed(pressureDecimals)} ${getUnitSymbol(unitPreferences.pressure_unit)}`;
      case 'temperature':
        return `${value.toFixed(temperatureDecimals)}${getUnitSymbol(unitPreferences.temperature_unit)}`;
      default:
        return value.toFixed(globalDecimals);
    }
  };

  // Modo frío: TODOS los valores pasan por el formateador central, así respetan
  // la unidad y los decimales configurados por el usuario/perfil. Los valores
  // llegan del backend en unidades canónicas (PSI / °C).
  const formatColdPressure = (psiValue, { showUnit = true } = {}) =>
    formatPressureValue(psiValue, unitPreferences, { showUnit });
  const formatColdTemperature = (celsius) => formatTemperatureValue(celsius, unitPreferences);

  const formatValueWithLimits = (value, metric, limits) => {
    const baseValue = formatValue(value, metric);
    if (!limits || (!limits.min && !limits.max)) return baseValue;

    const limitsText = [];
    if (limits.min !== null) limitsText.push(`${limits.min.toFixed(1)}`);
    if (limits.max !== null) limitsText.push(`${limits.max.toFixed(1)}`);

    return `${baseValue} (${limitsText.join('-')})`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t('hardcoded.components_alarms_alarmlist.sin_datos');
    return formatSmartDateTime(timestamp, formatUserDateTimeSync);
  };

  const baseTileStatus = getTileStatus();

  // En modo frío el color comunica el AJUSTE necesario, no la alarma:
  // verde OK, naranja requiere ajuste, rojo ajuste grande, gris sin dato.
  const COLD_STATUS_COLORS = {
    ok: theme.palette.success.main,
    subir: theme.palette.warning.main,
    bajar: theme.palette.warning.main,
    sin_reporte: theme.palette.grey[500],
    sin_sensor: theme.palette.grey[400],
  };
  const COLD_CRITICAL_DELTA_PSI = 15;   // desviacion a partir de la cual es critica

  // Sin lectura usable -> gris de conectividad (nunca rojo de alarma).
  const coldTileColor = () => {
    if (!coldPosition) return theme.palette.grey[400];
    if (coldPosition.psi === null || coldPosition.psi === undefined) {
      return COLD_STATUS_COLORS[coldPosition.status] || theme.palette.grey[500];
    }
    const needsAdjustment = ['subir', 'bajar'].includes(coldPosition.status);
    if (needsAdjustment && Math.abs(coldPosition.delta ?? 0) >= COLD_CRITICAL_DELTA_PSI) {
      return theme.palette.error.main;
    }
    return COLD_STATUS_COLORS[coldPosition.status] || theme.palette.grey[400];
  };

  const tileStatus = (coldMode && coldPosition)
    ? {
        ...baseTileStatus,
        shimmer: false,
        twinkle: false,
        bgColor: coldTileColor(),
      }
    : baseTileStatus;

  const getAnimationKeyframes = (bgColor) => `
    @keyframes tile-pulse {
      0% { 
        box-shadow: 0 0 0 0 ${bgColor}60;
        transform: scale(1);
      }
      70% { 
        box-shadow: 0 0 0 8px ${bgColor}00;
        transform: scale(1.02);
      }
      100% { 
        box-shadow: 0 0 0 0 ${bgColor}00;
        transform: scale(1);
      }
    }
    
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0.3; }
    }

    @keyframes shimmer {
      0% {
        background-position: -200px 0;
      }
      100% {
        background-position: calc(200px + 100%) 0;
      }
    }

    @keyframes twinkle {
      0%, 100% { 
        opacity: 1;
        transform: scale(1);
      }
      50% { 
        opacity: 0.85;
        transform: scale(1.03);
      }
    }

    @keyframes loading-dots {
      0%, 20% { opacity: 0; }
      50% { opacity: 1; }
      80%, 100% { opacity: 0; }
    }

    /* Estado combinado: sin reporte + alerta real pendiente → alterna gris ↔ rojo. */
    @keyframes offline-alert-blink {
      0%, 49% {
        background-color: #9e9e9e; /* gris (offline) */
      }
      50%, 100% {
        background-color: #d32f2f; /* rojo (alerta) */
      }
    }

    /* Indicador Dual parpadeante — sincronizado con offline-alert-blink (1.1s).
       Alterna entre rojo brillante y rojo apagado para no perder visibilidad. */
    @keyframes dual-indicator-blink {
      0%, 49% {
        box-shadow: inset 0 0 0 1px #000, inset 0 0 0 4px #d32f2f;
      }
      50%, 100% {
        box-shadow: inset 0 0 0 1px #000, inset 0 0 0 4px #d32f2f55;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      @keyframes offline-alert-blink { 0%,100% { background-color: #9e9e9e; } }
      @keyframes dual-indicator-blink { 0%,100% { box-shadow: inset 0 0 0 1px #000, inset 0 0 0 4px #d32f2f; } }
    }

    @keyframes colored-shimmer {
      0% {
        background-position: -200px 0;
        opacity: 0.7;
      }
      50% {
        opacity: 1;
      }
      100% {
        background-position: calc(200px + 100%) 0;
        opacity: 0.7;
      }
    }
  `;

  const renderTileContent = () => {
    // Modo frío. Arriba el PSI ACTUAL de la lectura; debajo, la flecha con el
    // ajuste necesario (⬆️/⬇️/✅). Una lectura vieja NO se oculta como
    // "sin reporte": se muestra igual y se marca con el punto de antigüedad.
    if (coldMode && coldPosition) {
      const hasReading = coldPosition.psi !== null && coldPosition.psi !== undefined;
      if (hasReading) {
        const arrowGlyph = coldPosition.status === 'subir' ? '▲'
          : coldPosition.status === 'bajar' ? '▼'
            : coldPosition.status === 'ok' ? '✓' : '';
        return (
          <Box sx={{ textAlign: 'center', width: '100%', lineHeight: 1 }}>
            <Typography component="div" sx={{
              fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 900, lineHeight: 1, color: 'inherit',
            }}>
              {formatColdPressure(coldPosition.psi, { showUnit: false })}
            </Typography>
            <Typography component="div" sx={{
              fontSize: { xs: '0.5rem', sm: '0.55rem' }, fontWeight: 700, lineHeight: 1.2,
              color: 'inherit', opacity: 0.95, mt: 0.15,
            }}>
              {arrowGlyph}
              {coldPosition.delta ? ` ${formatColdPressure(coldPosition.delta, { showUnit: false })}` : ''}
            </Typography>
          </Box>
        );
      }
      return (
        <Typography variant="caption" sx={{ fontSize: '0.5rem', textAlign: 'center', fontWeight: 700 }}>
          {coldPosition.status === 'sin_sensor'
            ? <>{i18n.t('hardcoded.components_schematics_positiontile.no_sensor')}</>
            : <>🛜</>}
        </Typography>
      );
    }

    if (tileStatus.status === 'loading' && tileStatus.shimmer) {
      return (
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(
            90deg,
            ${tileStatus.bgColor}00 0%,
            ${theme.palette.common.white}40 50%,
            ${tileStatus.bgColor}00 100%
          )`,
            animation: 'colored-shimmer 1.5s infinite'
          }
        }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.45rem',
              fontWeight: 700,
              textAlign: 'center',
              animation: 'loading-dots 1.5s infinite',
              zIndex: 1
            }}
          >
            ●●●
          </Typography>
        </Box>
      );
    }

    if (tileStatus.status === t('hardcoded.components_schematics_positiontile.no-sensor')) {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.5rem', textAlign: 'center', fontWeight: 700 }}>
          NO<br />SENSOR
        </Typography>
      );
    }

    // Plano sin telemetria: se muestra la posicion, que es la informacion que
    // aporta este esquematico (donde esta la alarma dentro del equipo).
    if (staticSnapshot && !effectiveData) {
      return (
        <Typography variant="body2" sx={{
          fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 900,
          lineHeight: 1, color: 'inherit', textAlign: 'center',
        }}>
          {position}
        </Typography>
      );
    }

    if (tileStatus.status === t('hardcoded.components_schematics_positiontile.no-data')) {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.5rem', textAlign: 'center', fontWeight: 700 }}>
          NO<br />DATA
        </Typography>
      );
    }

    switch (displayMode) {
      case 'temperature':
        if (convertedValues.temperature !== null && convertedValues.temperature !== undefined) {
          return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  fontWeight: 900,
                  lineHeight: 1,
                  opacity: 1,
                  color: 'inherit',
                  animation: tileStatus.twinkle ? 'twinkle 0.6s ease-in-out 2' : 'none'
                }}
              >
                {`${convertedValues.temperature.toFixed(0)}°`}
              </Typography>
            </Box>
          );
        } else {
          return (
            <Typography variant="caption" sx={{ fontSize: '0.5rem', textAlign: 'center', fontWeight: 700 }}>
              NO<br />TEMP
            </Typography>
          );
        }

      case 'hours':
        if (effectiveData?.timestamp) {
          return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  fontWeight: 900,
                  lineHeight: 1,
                  opacity: 1,
                  color: 'inherit',
                  animation: tileStatus.twinkle ? 'twinkle 0.6s ease-in-out 2' : 'none'
                }}
              >
                {formattedHours}
              </Typography>
            </Box>
          );
        } else {
          return (
            <Typography variant="caption" sx={{ fontSize: '0.5rem', textAlign: 'center', fontWeight: 700 }}>
              NO<br />TIME
            </Typography>
          );
        }

      default:
        if (convertedValues.pressure !== null && convertedValues.pressure !== undefined) {
          return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 900,
                  lineHeight: 1,
                  opacity: 1,
                  color: 'inherit',
                  animation: tileStatus.twinkle ? 'twinkle 0.6s ease-in-out 2' : 'none'
                }}
              >
                {convertedValues.pressure.toFixed(0)}
              </Typography>
            </Box>
          );
        }
    }

    return <SensorIcon sx={{ fontSize: 12, opacity: 0.8, color: 'inherit' }} />;
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: getAnimationKeyframes(tileStatus.bgColor) }} />

      <Box
        sx={{
          width: { xs: 32, sm: 38 },
          height: { xs: 32, sm: 38 },
          border: '2px solid',
          borderColor: tileStatus.borderColor,
          borderRadius: 0.75,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: tileStatus.bgColor,
          color: tileStatus.color,
          transition: 'all 0.2s ease-in-out',
          cursor: (hasAssignment && !showInitialShimmer && !isLoadingData && !unitPreferencesLoading) ? 'pointer' : 'default',
          fontWeight: 700,
          fontSize: { xs: '0.65rem', sm: '0.75rem' },
          position: 'relative',
          /* animation se compone abajo combinando offline-blink, dual-blink y tile-pulse */
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          boxShadow: isHovered ? theme.shadows[4] :
            (tileStatus.status === 'ticket-alarm' || tileStatus.status === 'alarm') ? theme.shadows[3] :
              theme.shadows[1],
          flexShrink: 0,
          '&:hover': (hasAssignment && !showInitialShimmer && !isLoadingData && !unitPreferencesLoading) ? {
            boxShadow: theme.shadows[6],
          } : {},
          ...(tileStatus.shimmer && {
            background: `linear-gradient(
              90deg,
              ${tileStatus.bgColor} 25%,
              ${tileStatus.bgColor}dd 50%,
              ${tileStatus.bgColor} 75%
            )`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite'
          }),
          // YOYO/DUAL HIGHLIGHT — parpadea siempre que esté presente, sincronizado
          // con el parpadeo offline+alert (mismo periodo de 1.1s).
          ...(yoyoGroupInfo.inGroup && !suppressYoyoOutline && {
            boxShadow: `inset 0 0 0 1px ${theme.palette.common.black}, inset 0 0 0 4px ${theme.palette.error.main}`,
          }),
          // NO-REPORT alone: borde naranja discreto interior, sin agrandar ni desplazar.
          ...(_onlyNoReport && !yoyoGroupInfo.inGroup && {
            boxShadow: `inset 0 0 0 2px ${theme.palette.warning.main}`,
          }),
          // Animaciones combinadas: cuando hay yoyo Y/O offline+alert, las encadenamos
          // para que parpadeen al mismo tiempo (período 1.1s para ambas).
          animation: [
            (_hasRealAlertPending && _hasNoReport) && 'offline-alert-blink 1.1s infinite',
            (yoyoGroupInfo.inGroup && !suppressYoyoOutline) && 'dual-indicator-blink 1.1s infinite',
            (tileStatus.pulse) && 'tile-pulse 1.5s infinite',
          ].filter(Boolean).join(', ') || 'none',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: 1,
            left: 2,
            fontSize: { xs: '0.35rem', sm: '0.4rem' },
            fontWeight: 400,
            opacity: tileStatus.status === 'loading' ? 0.5 : 0.7,
            color: 'inherit'
          }}
        >
          {position}
        </Typography>

        {/* Badge "YOYO" removido — la indicación ahora es sólo el borde rojo + outline negro. */}

        {activeReasonBadges.length === 0 && isRealtimeFromWebSocket && tileStatus.status === 'normal' && !shouldTwinkle && (
          <Box
            sx={{
              position: 'absolute',
              top: 1,
              right: 2,
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: 'white',
              opacity: 0.8,
              animation: 'blink 2s infinite'
            }}
          />
        )}

        {/* Modo frio: marca discreta de "lectura no reciente" / "sin reporte".
            La lectura NO se oculta; solo se avisa de su antiguedad. */}
        {coldMode && coldPosition && coldPosition.psi !== null && coldPosition.psi !== undefined
          && (coldPosition.comm_status === 'stale' || coldPosition.comm_status === 'no_report') && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 1,
              right: 2,
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: coldPosition.comm_status === 'no_report' ? '#212121' : '#ffffff',
              opacity: 0.85,
              border: '1px solid rgba(255,255,255,0.6)',
            }}
          />
        )}

        {renderTileContent()}

        {tileStatus.status === 'loading' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <CircularProgress
              size={{ xs: 4, sm: 5 }}
              sx={{
                color: 'inherit',
                opacity: 0.7
              }}
            />
          </Box>
        )}
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="top"
        transition
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
        sx={{ zIndex: 1301 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Card elevation={6} sx={{ width: 280, maxWidth: '85vw', borderRadius: 1.5 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                    {i18n.t('alarms.list.position')}{position}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isRealtimeFromWebSocket && (
                      <Chip
                        icon={<WifiIcon sx={{ fontSize: 12 }} />}
                        label="LIVE"
                        size="small"
                        color="success"
                        sx={{ height: 18, fontSize: '0.6rem' }}
                      />
                    )}
                    <SensorIcon sx={{ color: 'secondary.main', fontSize: 14 }} />
                    <Typography variant="caption" fontWeight={500} fontSize="0.65rem">
                      {assignedSensor?.sensor_type
                        ? `${t('hardcoded.components_schematics_positiontile.sensor')} ${assignedSensor.sensor_type}`
                        : t('hardcoded.components_schematics_positiontile.sin_sensor')}
                    </Typography>
                  </Box>
                </Box>

                {coldMode && coldPosition && (
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                        {t('hardcoded.components_schematics_positiontile.presion_en_frio')}</Typography>

                      {coldPosition.psi !== null && coldPosition.psi !== undefined && (
                        <Typography variant="body2">
                          {i18n.t('hardcoded.components_schematics_positiontile.actual')}
                          <strong>{formatColdPressure(coldPosition.psi)}</strong>
                          {coldPosition.temp !== null && coldPosition.temp !== undefined
                            && ` · ${formatColdTemperature(coldPosition.temp)}`}
                        </Typography>
                      )}

                      {coldPosition.psi_cold !== null && coldPosition.psi_cold !== undefined && (
                        <Typography variant="body2">
                          {t('hardcoded.components_schematics_positiontile.frio_est')}
                          <strong>{formatColdPressure(coldPosition.psi_cold)}</strong>
                        </Typography>
                      )}

                      {coldPosition.ideal !== null && coldPosition.ideal !== undefined && (
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          {t('hardcoded.components_schematics_positiontile.ideal_frio')}
                          <strong>{formatColdPressure(coldPosition.ideal)}</strong>
                        </Typography>
                      )}

                      {/* Accion recomendada: viene calculada del backend; aqui solo se
                          formatea el delta con las unidades y decimales del usuario. */}
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {t('hardcoded.components_schematics_positiontile.accion')}{' '}
                        {coldPosition.icon}{' '}
                        {coldPosition.status === 'subir'
                          ? `${t('hardcoded.components_schematics_positiontile.subir')} ${formatColdPressure(coldPosition.delta)}`
                          : coldPosition.status === 'bajar'
                            ? `${t('hardcoded.components_schematics_positiontile.bajar')} ${formatColdPressure(coldPosition.delta)}`
                            : coldPosition.summary}
                      </Typography>

                      {coldPosition.comm_status === 'no_report' && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600 }}>
                          {t('hardcoded.components_schematics_positiontile.revisar_sensor_comunicacion')}
                        </Typography>
                      )}

                      {coldPosition.stale && coldPosition.age_label && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {t('hardcoded.components_schematics_positiontile.lectura_no_reciente')}{coldPosition.age_label})
                        </Typography>
                      )}
                    </Box>
                  </>
                )}

                {(positionOpenTicketsAll.length > 0 || positionAcknowledgedTicketsAll.length > 0) && (
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ mb: 1 }}>
                      {positionOpenTicketsAll.length > 0 && (
                        <Chip
                          label={t('hardcoded.components_schematics_positiontile.length_tickets_abiertos', { length: positionOpenTicketsAll.length })}
                          color="error"
                          size="small"
                          sx={{ fontSize: '0.6rem', mb: 0.5, mr: 0.5 }}
                        />
                      )}
                      {positionAcknowledgedTicketsAll.length > 0 && (
                        <Chip
                          label={t('hardcoded.components_schematics_positiontile.length_tickets_vistos', { length: positionAcknowledgedTicketsAll.length })}
                          color="warning"
                          size="small"
                          sx={{ fontSize: '0.6rem', mb: 0.5 }}
                        />
                      )}
                    </Box>
                  </>
                )}

                <Divider sx={{ mb: 1 }} />

                {effectiveData && !showInitialShimmer && !isLoadingData && !unitPreferencesLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayMode === 'hours' && effectiveData?.timestamp && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ color: 'secondary.main', fontSize: 14 }} />
                          <Typography
                            variant="caption"
                            color="secondary.main"
                            fontWeight={600}
                            fontSize="0.7rem"
                          >
                            {formattedHours} {t('hardcoded.components_schematics_positiontile.desde_actualizacion')}</Typography>
                        </Box>
                      </Box>
                    )}

                    {convertedValues.pressure !== null && convertedValues.pressure !== undefined && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PressureIcon sx={{
                            color: getMetricColorAndStatus('pressure', convertedValues.pressure).color,
                            fontSize: 14
                          }} />
                          <Typography
                            variant="caption"
                            color={getMetricColorAndStatus('pressure', convertedValues.pressure).color}
                            fontWeight={500}
                            fontSize="0.7rem"
                          >
                            {(() => {
                              const pressureInfo = getMetricColorAndStatus('pressure', convertedValues.pressure);
                              return formatValueWithLimits(convertedValues.pressure, 'pressure', pressureInfo.limits);
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {convertedValues.temperature !== null && convertedValues.temperature !== undefined && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TemperatureIcon sx={{
                            color: getMetricColorAndStatus('temperature', convertedValues.temperature).color,
                            fontSize: 14
                          }} />
                          <Typography
                            variant="caption"
                            color={getMetricColorAndStatus('temperature', convertedValues.temperature).color}
                            fontWeight={500}
                            fontSize="0.7rem"
                          >
                            {(() => {
                              const tempInfo = getMetricColorAndStatus('temperature', convertedValues.temperature);
                              return formatValueWithLimits(convertedValues.temperature, 'temperature', tempInfo.limits);
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Divider />

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimeIcon sx={{ color: 'text.secondary', fontSize: 12 }} />
                        <Typography variant="caption" color="text.secondary" fontSize="0.6rem">
                          {formatTimestamp(effectiveData?.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : showInitialShimmer || isLoadingData || unitPreferencesLoading ? (
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <CircularProgress size={16} sx={{ mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                      {unitPreferencesLoading ? t('hardcoded.components_schematics_positiontile.cargando_unidades') : t('hardcoded.components_schematics_positiontile.cargando_datos')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                      {t('hardcoded.components_alarms_alarmlist.sin_datos')}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        )}
      </Popper>
    </>
  );
};

// 🔥 FIX 1: React.memo con comparación personalizada
const MemoizedPositionTile = React.memo(PositionTile, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian los datos de ESTA posición específica
  return (
    prevProps.position === nextProps.position &&
    prevProps.realtimeData === nextProps.realtimeData &&
    prevProps.lastAvailableData === nextProps.lastAvailableData &&
    prevProps.isInitialLoading === nextProps.isInitialLoading &&
    prevProps.isLoadingData === nextProps.isLoadingData &&
    prevProps.displayMode === nextProps.displayMode &&
    prevProps.staticSnapshot === nextProps.staticSnapshot &&
    prevProps.coldMode === nextProps.coldMode &&
    prevProps.coldPosition === nextProps.coldPosition &&
    prevProps.alarms.length === nextProps.alarms.length &&
    prevProps.alarmStatuses.length === nextProps.alarmStatuses.length &&
    prevProps.positionTickets.length === nextProps.positionTickets.length
  );
});

MemoizedPositionTile.displayName = i18n.t('hardcoded.components_schematics_positiontile.positiontile');

// PositionGrid component
const PositionGrid = ({
  vehicle,
  realtimeDataMap = {},
  lastAvailableDataMap = {},
  alarms = [],
  alarmStatuses = [],
  onTelemetryClick,
  isInitialLoading = false,
  isLoadingData = false,
  lastRefresh = null,
  displayMode = 'pressure'
}) => {
  const { t } = useTranslation();
  const availablePositions = vehicle?.vehicle_sensors
    ?.map(vs => vs.position)
    .sort((a, b) => a - b) || [];

  if (availablePositions.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        border: 2,
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'grey.50'
      }}>
        <Typography variant="body2" color="text.secondary">
          {t('hardcoded.components_schematics_positiontile.no_hay_posiciones_de_sensores')}</Typography>
      </Box>
    );
  }

  const maxCols = 6;
  const cols = Math.min(availablePositions.length, maxCols);

  return (
    <Box sx={{
      border: 2,
      borderColor: 'primary.main',
      borderRadius: 2,
      p: 2,
      bgcolor: 'background.paper',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2
      }}>
        <Typography variant="h6" fontWeight={600}>
          {vehicle?.display_name || t('vehicles.table.vehicle')} {t('hardcoded.components_schematics_positiontile.-_vista_en_tiempo_real')}{displayMode === 'hours' && t('hardcoded.components_schematics_positiontile.modo_horas')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {availablePositions.length} {t('hardcoded.components_schematics_positiontile.posiciones')}{alarmStatuses.filter(a => a.status === 'CRITICAL').length} {t('hardcoded.components_schematics_positiontile.alarmas_criticas')}{lastRefresh && ` | ${new Date(lastRefresh).toLocaleTimeString()}`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(auto-fit, minmax(40px, 1fr))',
          sm: `repeat(${Math.min(cols, 4)}, 1fr)`,
          md: `repeat(${Math.min(cols, 6)}, 1fr)`,
        },
        gap: 1,
        justifyItems: 'center',
        alignItems: 'center',
        maxWidth: '100%',
        mx: 'auto',
        overflow: 'hidden'
      }}>
        {availablePositions.map(position => {
          return (
            <MemoizedPositionTile
              key={position}
              vehicle={vehicle}
              position={position}
              realtimeData={realtimeDataMap[position]}
              lastAvailableData={lastAvailableDataMap[position]}
              alarms={alarms}
              alarmStatuses={alarmStatuses}
              positionTickets={[]}
              onTelemetryClick={onTelemetryClick}
              isInitialLoading={isInitialLoading}
              isLoadingData={isLoadingData}
              displayMode={displayMode}
            />
          );
        })}
      </Box>

      <Box sx={{
        mt: 2,
        pt: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="success.main" fontWeight={600} fontSize="0.65rem">
            ● Normal
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="error.main" fontWeight={600} fontSize="0.65rem">
            {t('hardcoded.components_schematics_positiontile.alarma_critica')}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="grey.600" fontWeight={600} fontSize="0.65rem">
            {t('hardcoded.components_schematics_positiontile.datos_historicos')}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="primary.main" fontWeight={600} fontSize="0.65rem">
            {i18n.t('hardcoded.components_schematics_positiontile.cargando')}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MemoizedPositionTile;
export { PositionGrid };